import { type SelectProps } from 'antd';
import { debounce } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import { type UserPreviewDataFragment } from '../../../api/fragments/UserPreviewData';
import { useGetUsersOptionsForUserGroupLazyQuery } from '../../../api/queries/getUsersOptionsForUserGroup';
import SearchUserWithTableField from '../../../components/fields/SearchUserWithTableField';

export type SearchUserWithTableFieldByUserGroupProps = {
    name: string;
    userGroup: UserGroupSpecsFragment;
    debounceTimeout: number;
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.children.toString().toLowerCase().includes(inputValue.toLowerCase());

const SearchUserWithTableFieldByUserGroup = ({
    name,
    userGroup,
    debounceTimeout,
}: SearchUserWithTableFieldByUserGroupProps) => {
    const userGroupId = userGroup.id;

    const [userOptions, setUserOptions] = useState<UserPreviewDataFragment[]>([]);
    const [getUserOptions, { loading }] = useGetUsersOptionsForUserGroupLazyQuery({
        fetchPolicy: 'network-only',
    });

    const debounceFetcher = useMemo(() => {
        const loadUserOptions = async () => {
            const { data } = await getUserOptions({ variables: { userGroupId } });
            const options = data?.users || [];
            setUserOptions(options);
        };

        return debounce(debounceTimeout, loadUserOptions);
    }, [debounceTimeout, getUserOptions, userGroupId]);

    return (
        <SearchUserWithTableField
            filterOption={filterOption}
            loading={loading}
            mode="multiple"
            name={name}
            onSearch={debounceFetcher}
            style={{ width: '100%' }}
            users={userOptions}
        />
    );
};

export default SearchUserWithTableFieldByUserGroup;
